import { AAGUID } from '@interticket/core';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { take, map } from 'rxjs/operators';
import { SearchParams } from '@core/models/query-params/search-params.model';
import { ListResult } from '@core/models/list-result/list-result.model';
import { IListResult } from '@core/interfaces/list-result/list-result.interface';
import { IEventsSource } from '../../interfaces/event/events-source.interface';
import { IEventView } from '../../interfaces/event/event-view.interface';
import { EventView } from '../../models/event/event-view.model';
import { EventFilter } from '../../models/event/event-filter.model';
import { EventListItem } from '../../models/event/event-list-item.model';
import { IEventListItem } from '../../interfaces/event/event-list-item.interface';
import { EventViewBase } from '@core/models/event/event-view-base.model';
import { IEventViewBase } from '@core/interfaces/event/event-view-base.interface';
import { parseArray } from '@interticket/core';
import { CartItem } from '@core/models/cart/cart-item.model';
import { CartStoreService } from '../cart/cart-store.service';
import { IPriceCategory } from '@core/interfaces/product/pricing-category.interface';

@Injectable()
export class EventsService {

  constructor(
    private eventSourceService: IEventsSource,
    private cartStoreService: CartStoreService
  ) { }

  private setPriceCategoryAmounts(iEvent: IEventView): IEventView {
    if (iEvent.priceCategories) {
      iEvent.priceCategories.forEach(priceCategory => priceCategory.amount = this.getPriceCategoryAmount(priceCategory));
    }
    return iEvent;
  }

  private getPriceCategoryAmount(priceCategory: IPriceCategory): number {
    const cart = this.cartStoreService.cart;

    if (Array.isArray(cart?.cartItems)) {
      return cart.cartItems.filter((cartItem: CartItem) => cartItem.priceCategoryId === priceCategory.id).length;
    }

    return 0;
  }

  getEvent(eventId: AAGUID): Observable<EventView> {
    return this.eventSourceService.getEvent(eventId)
      .pipe(
        take(1),
        map((iEvent: IEventView) => new EventView(this.setPriceCategoryAmounts(iEvent)))
      );
  }

  getList(searchParams?: SearchParams, filter?: EventFilter): Observable<ListResult<IEventListItem, EventListItem>> {
    return this.eventSourceService.getList(searchParams, filter)
      .pipe(
        take(1),
        map((iListResult: IListResult<IEventListItem>) => new ListResult<IEventListItem, EventListItem>(iListResult, EventListItem))
      );
  }

  getMoreEvents(id: AAGUID): Observable<ListResult<IEventViewBase, EventViewBase>> {
    return this.eventSourceService.getMoreEvents(id)
      .pipe(
        take(1),
        map((iListResult: IListResult<IEventViewBase>) => new ListResult<IEventViewBase, EventViewBase>(iListResult, EventViewBase))
      );
  }

  getListByIds(eventIds: AAGUID[]): Observable<EventView[]> {
    return this.eventSourceService.getListByIds(eventIds)
      .pipe(
        take(1),
        map((iEventViews: IEventView[]) => parseArray<IEventView, EventView>(iEventViews.map(this.setPriceCategoryAmounts.bind(this)), EventView))
      );
  }

  getCalendarEvents(): Observable<IListResult<IEventViewBase>> {
    return this.eventSourceService.getCalendarEvents()
      .pipe(
        take(1),
        map((iListResult: IListResult<IEventViewBase>) => new ListResult<IEventViewBase, EventViewBase>(iListResult, EventViewBase))
      );
  }

}
